import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { PosTopBar } from '@/components/pos/PosTopBar.js';
import { PosItemSelection } from '@/components/pos/PosItemSelection.js';
import { PosCartPanel } from '@/components/pos/PosCartPanel.js';
import { PosCustomerPanel } from '@/components/pos/PosCustomerPanel.js';
import { PosCheckoutModal } from '@/components/pos/PosCheckoutModal.js';
import { CommandPalette } from '@/components/CommandPalette.js';
import { broadcastToDisplay } from '@/lib/api/display-staff.js';
import { cashSessionsApi } from '@/lib/api/cash-sessions.js';
import { getCurrentStore } from '@/lib/api/stores.js';
import { buildPosDisplayEvent } from '@/lib/pos/display.js';
import { useAuthStore } from '@/stores/auth.js';
import { usePosCartStore } from '@/stores/pos-cart.js';
import { usePosUiStore } from '@/stores/pos-ui.js';

export function POSPage() {
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [quoteSearchOpen, setQuoteSearchOpen] = useState(false);

  const authStoreId = useAuthStore((s) => s.user?.storeId);
  const setStoreId = usePosCartStore((s) => s.setStoreId);
  const items = usePosCartStore((s) => s.items);
  const customer = usePosCartStore((s) => s.customer);
  const cartDiscount = usePosCartStore((s) => s.cartDiscount);
  const setActiveCategory = usePosUiStore((s) => s.setActiveCategory);

  const storeQuery = useQuery({
    queryKey: ['current-store'],
    queryFn: getCurrentStore,
  });

  const cashQuery = useQuery({
    queryKey: ['cash-session-current'],
    queryFn: cashSessionsApi.getCurrent,
    refetchInterval: 60_000,
  });

  const store = storeQuery.data;

  useEffect(() => {
    const storeId = store?.id ?? authStoreId;
    if (storeId) setStoreId(storeId);
  }, [store?.id, authStoreId, setStoreId]);

  useEffect(() => {
    if (!store) return;
    const defaultCat = store.posDefaultCategory ?? 'all';
    setActiveCategory(defaultCat as 'all');
  }, [store?.id, store?.posDefaultCategory, setActiveCategory]);

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setQuoteSearchOpen(true);
      }
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  useEffect(() => {
    const storeId = store?.id ?? authStoreId;
    if (!storeId || checkoutOpen) return;

    if (items.length === 0) {
      void broadcastToDisplay(storeId, {
        type: 'display.update',
        payload: { mode: 'idle' },
        timestamp: new Date().toISOString(),
      });
      return;
    }

    void broadcastToDisplay(
      storeId,
      buildPosDisplayEvent({
        items,
        customer,
        cartDiscount,
        taxRate: Number(store?.taxRate ?? 0),
        taxMode: store?.taxMode ?? 'parts_only',
        storeName: store?.name ?? 'Store',
      }),
    );
  }, [items, customer, cartDiscount, store, authStoreId, checkoutOpen]);

  if (storeQuery.isLoading || !store) {
    return (
      <div className="flex h-screen items-center justify-center overflow-hidden bg-slate-100 text-slate-500">
        Loading POS…
      </div>
    );
  }

  return (
    <div className="flex h-screen max-h-screen flex-col overflow-hidden bg-slate-100">
      <PosTopBar
        storeName={store.name}
        cashSession={cashQuery.data}
        onOpenQuoteSearch={() => setQuoteSearchOpen(true)}
      />
      {/* min-h-0 lets grid children shrink so overflow-y-auto works */}
      <div className="grid min-h-0 flex-1 grid-cols-1 overflow-hidden md:grid-cols-[minmax(0,35fr)_minmax(0,35fr)_minmax(0,30fr)]">
        <PosItemSelection store={store} />
        <PosCartPanel store={store} />
        <PosCustomerPanel store={store} onCheckout={() => setCheckoutOpen(true)} />
      </div>
      <PosCheckoutModal
        open={checkoutOpen}
        store={store}
        onClose={() => setCheckoutOpen(false)}
        onComplete={() => setCheckoutOpen(false)}
      />
      <CommandPalette open={quoteSearchOpen} onClose={() => setQuoteSearchOpen(false)} />
    </div>
  );
}
